import { BuyQuoteResponse, SellQuoteResponse } from '@src/types/coinbase'

interface OnrampURLParams {
    sessionToken?: string
    projectId: string
    walletAddress?: string
    network: string
    buyAsset: string
    presetCryptoAmount?: string
    presetFiatAmount?: string
    fiatCurrency?: string
    buyQuote?: BuyQuoteResponse
    paymentMethod?: string
    partnerUserId?: string
}

interface OfframpURLParams {
    sessionToken?: string
    projectId: string
    walletAddress?: string
    networks: string[]
    partnerUserId?: string
    redirectUrl?: string
    sellQuote?: SellQuoteResponse
    paymentMethod?: string
    defaultCashoutMethod?: string
}

/**
 * Generate a Coinbase offramp URL with all required parameters
 *
 * Documentation: https://docs.cdp.coinbase.com/onramp/docs/api-offramp-overview
 */
export const getOfframpSellURL = ({
    sessionToken,
    projectId,
    walletAddress,
    networks,
    partnerUserId,
    redirectUrl = 'https://app.mach.exchange/',
    sellQuote,
    paymentMethod,
    defaultCashoutMethod,
}: OfframpURLParams): string => {
    // Use wallet address as partnerUserId when available
    const userId = walletAddress || partnerUserId || 'user'
    const OFFRAMP_PAY_API_HOST = 'pay.coinbase.com'

    // Return empty URL if no session token is provided
    if (!sessionToken) {
        console.warn('No session token provided for offramp URL generation')
        return ''
    }

    // Start with base parameters
    const params = new URLSearchParams({
        sessionToken,
        appId: projectId,
        partnerUserId: userId,
        redirectUrl,
    })

    // Add payment method if specified
    if (paymentMethod) {
        params.append('defaultPaymentMethod', paymentMethod)
    }

    // Add default cashout method if specified
    if (defaultCashoutMethod) {
        params.append('defaultCashoutMethod', defaultCashoutMethod)
    }

    // Add addresses parameter with the required format
    if (walletAddress && networks && networks.length > 0) {
        // Create a networks JSON object in the required format
        const networksJson = JSON.stringify({
            [walletAddress]: networks,
        })
        params.append('addresses', networksJson)
    }

    // Add quote ID if available
    if (sellQuote?.quote_id) {
        params.append('quoteId', sellQuote.quote_id)
    }

    // Build the final URL - note this uses v3 instead of v1 used in onramp
    return `https://${OFFRAMP_PAY_API_HOST}/v3/sell/input?${params.toString()}`
}

/**
 * Generate a Coinbase onramp URL with all required parameters
 *
 * Documentation: https://docs.cdp.coinbase.com/onramp/docs/api-onramp-initializing#onramp-url-parameters
 */
export const getOnrampBuyURL = ({
    sessionToken,
    projectId,
    walletAddress,
    network,
    buyAsset,
    presetCryptoAmount,
    presetFiatAmount,
    fiatCurrency = 'USD',
    buyQuote,
    paymentMethod,
    partnerUserId,
}: OnrampURLParams): string => {
    // Use wallet address as partnerUserId when available
    const userId = walletAddress || partnerUserId || 'user'
    const ONRAMP_PAY_API_HOST = 'pay.coinbase.com'

    // Return empty URL if no session token is provided
    if (!sessionToken) {
        console.warn('No session token provided for onramp URL generation')
        return ''
    }

    // Start with base parameters
    const params = new URLSearchParams({
        sessionToken,
        appId: projectId,
        defaultExperience: 'buy',
        redirectUrl: 'https://app.mach.exchange/',
        partnerUserId: userId,
    })

    // Add default payment method
    params.append('defaultPaymentMethod', paymentMethod || 'CARD')

    // Add addresses parameter with the required format
    if (walletAddress) {
        params.append('addresses', `{"${walletAddress}":["${network}"]}`)
    }

    // Add assets parameter with the required format
    params.append('assets', `["${buyAsset}"]`)

    // Add optional parameters if available
    if (fiatCurrency) {
        params.append('fiatCurrency', fiatCurrency)
    }

    // Set preset amount values
    if (presetFiatAmount) {
        params.append('presetFiatAmount', presetFiatAmount)
    }

    // If buy quote is available, use its purchase amount as presetCryptoAmount
    if (buyQuote?.purchase_amount?.value) {
        params.append('presetCryptoAmount', buyQuote.purchase_amount.value)
    } else if (presetCryptoAmount) {
        params.append('presetCryptoAmount', presetCryptoAmount)
    }

    // Build the final URL
    return `https://${ONRAMP_PAY_API_HOST}/buy/select-asset?${params.toString()}`
}
